import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { reportsService } from '../../services/reportsService';
import ReportBody from '../../components/Reports/ReportBody';
import { 
  BarChart3, 
  FileText, 
  Download, 
  Calendar,
  Filter,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Package,
  CreditCard,
  Loader2,
  AlertCircle,
  Sparkles,
  Search,
  X
} from 'lucide-react';

const Reports = () => {
  const { user, hasRole } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState({});
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    customerId: '',
    status: ''
  });

  // Natural-language query bar state
  const [nlqInput, setNlqInput] = useState('');
  const [nlqLoading, setNlqLoading] = useState(false);
  const [nlqResult, setNlqResult] = useState(null); // { interpretation, unmatched, applied }
  const [nlqError, setNlqError] = useState(null);

  // Report body presentation: heavy charts or a sortable data (table) view.
  const [reportView, setReportView] = useState('charts');

  const NLQ_EXAMPLES = [
    'Approved chargebacks last month',
    'Rejected claims for customer Cardinal Health this year',
    'Financial summary by month',
    'Claims in error in the last 30 days',
    'Batch processing stats last week'
  ];

  // Tabs the Reports screen can render (system-audit has no live tab here).
  const NLQ_SUPPORTED_TABS = ['chargebacks', 'contracts', 'customers', 'products', 'financial', 'edi'];

  // Run a plain-English question: translate → apply filters → open the report.
  const runNlq = async (question) => {
    const q = (question ?? nlqInput).trim();
    if (!q) return;
    setNlqLoading(true);
    setNlqError(null);
    try {
      const res = await reportsService.parseQuery(q);
      const data = res.data?.data || res.data;
      const applied = data.applied || { tab: 'chargebacks', filters: {} };
      setNlqResult({
        interpretation: data.interpretation,
        unmatched: data.unmatched || [],
        report: data.report,
        applied
      });

      if (NLQ_SUPPORTED_TABS.includes(applied.tab)) {
        // Reset to base filters, then apply what the parser resolved.
        const merged = { startDate: '', endDate: '', customerId: '', status: '', ...applied.filters };
        setFilters(merged);
        setActiveTab(applied.tab);
        fetchReportData(applied.tab, merged);
      }
    } catch (err) {
      console.error('NLQ error:', err);
      setNlqError(err.response?.data?.error || 'Could not interpret that question. Try rephrasing.');
    } finally {
      setNlqLoading(false);
    }
  };

  const clearNlq = () => {
    setNlqInput('');
    setNlqResult(null);
    setNlqError(null);
  };

  const reportSections = [
    {
      id: 'overview',
      name: 'Reports Overview',
      icon: BarChart3,
      description: 'Summary of available reports and analytics',
      roles: ['ADMIN', 'STAFF', 'CUSTOMER']
    },
    {
      id: 'chargebacks',
      name: 'Chargeback Reports',
      icon: CreditCard,
      description: 'Chargeback analysis and reporting',
      roles: ['ADMIN', 'STAFF', 'CUSTOMER']
    },
    {
      id: 'contracts',
      name: 'Contract Reports',
      icon: FileText,
      description: 'Contract performance and analytics',
      roles: ['ADMIN', 'STAFF']
    },
    {
      id: 'customers',
      name: 'Customer Reports',
      icon: Users,
      description: 'Customer activity and relationship reports',
      roles: ['ADMIN', 'STAFF']
    },
    {
      id: 'products',
      name: 'Product Reports',
      icon: Package,
      description: 'Product performance and pricing reports',
      roles: ['ADMIN', 'STAFF']
    },
    {
      id: 'financial',
      name: 'Financial Reports',
      icon: DollarSign,
      description: 'Financial performance and revenue reports',
      roles: ['ADMIN', 'STAFF']
    },
    {
      id: 'edi',
      name: 'EDI Reports',
      icon: BarChart3,
      description: 'EDI transaction and performance reports',
      roles: ['ADMIN', 'STAFF']
    }
  ];

  // Fetch report data based on active tab. Accepts an optional filter override
  // so callers (e.g. the natural-language bar) don't race the async setFilters.
  const fetchReportData = async (reportType, overrideFilters) => {
    setIsLoading(true);
    setError(null);
    const f = overrideFilters || filters;

    try {
      let data;
      switch (reportType) {
        case 'chargebacks':
          data = await reportsService.getChargebackSummary(f);
          break;
        case 'customers':
          data = await reportsService.getCustomerAnalysis(f);
          break;
        case 'products':
          data = await reportsService.getProductPerformance(f);
          break;
        case 'contracts':
          data = await reportsService.getContractPerformance(f);
          break;
        case 'financial':
          data = await reportsService.getFinancialSummary(f);
          break;
        case 'edi':
          data = await reportsService.getBatchProcessing(f);
          break;
        default:
          data = await reportsService.getAvailableReports();
      }
      
      setReportData(prev => ({
        ...prev,
        [reportType]: data.data || data
      }));
    } catch (err) {
      console.error('Error fetching report data:', err);
      setError(err.response?.data?.message || 'Failed to fetch report data');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle tab change
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (tabId !== 'overview') {
      fetchReportData(tabId);
    }
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Export report
  const handleExportReport = async (reportType, format = 'pdf') => {
    setIsLoading(true);
    try {
      const response = await reportsService.exportReport(reportType, format, filters);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}-report.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting report:', err);
      setError('Failed to export report');
    } finally {
      setIsLoading(false);
    }
  };

  const getAccessibleSections = () => {
    return reportSections.filter(section => {
      if (section.roles.includes('ADMIN')) return true;
      if (section.roles.includes('STAFF') && hasRole('STAFF')) return true;
      if (section.roles.includes('CUSTOMER') && hasRole('CUSTOMER')) return true;
      return false;
    });
  };

  const accessibleSections = getAccessibleSections();

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center">
                  <FileText className="h-8 w-8 text-blue-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Available Reports</p>
                    <p className="text-2xl font-semibold text-gray-900">{accessibleSections.length}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center">
                  <Download className="h-8 w-8 text-green-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Downloads Today</p>
                    <p className="text-2xl font-semibold text-gray-900">0</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center">
                  <Calendar className="h-8 w-8 text-purple-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Last Generated</p>
                    <p className="text-2xl font-semibold text-gray-900">Never</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" title="Generate Report">
                  <Download className="h-6 w-6 text-blue-500 mr-3" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900">Generate Report</p>
                    <p className="text-sm text-gray-500">Create a new custom report</p>
                  </div>
                </button>
                <button className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" title="Schedule Report">
                  <Calendar className="h-6 w-6 text-green-500 mr-3" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900">Schedule Report</p>
                    <p className="text-sm text-gray-500">Set up automated reporting</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        );

      case 'chargebacks':
        return (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Chargeback Report Filters</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All Statuses</option>
                    <option value="IMPORTED">Imported</option>
                    <option value="VALIDATED">Validated</option>
                    <option value="IN_ERROR">In Error</option>
                    <option value="APPROVED">Approved</option>
                    <option value="REJECTED">Rejected</option>
                    <option value="POSTED">Posted</option>
                    <option value="SUPERSEDED">Superseded</option>
                    <option value="CANCELLED">Cancelled</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => fetchReportData('chargebacks')}
                    disabled={isLoading}
                    className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                    title="Apply Filters"
                  >
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Filter className="h-4 w-4 mr-2" />}
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>

            {/* Report Content */}
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Chargeback Summary Report</h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleExportReport('chargebacks', 'pdf')}
                    disabled={isLoading}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                    title="Export PDF"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export PDF
                  </button>
                  <button
                    onClick={() => handleExportReport('chargebacks', 'csv')}
                    disabled={isLoading}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    title="Export CSV"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </button>
                </div>
              </div>

              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                  <span className="ml-2 text-gray-600">Loading report data...</span>
                </div>
              ) : (
                <ReportBody type="chargebacks" data={reportData.chargebacks} view={reportView} />
              )}
            </div>
          </div>
        );

      case 'contracts':
        return (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Contract Performance Report</h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleExportReport('contracts', 'pdf')}
                    disabled={isLoading}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                    title="Export PDF"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export PDF
                  </button>
                </div>
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                  <span className="ml-2 text-gray-600">Loading report data...</span>
                </div>
              ) : (
                <ReportBody type="contracts" data={reportData.contracts} view={reportView} />
              )}
            </div>
          </div>
        );

      case 'customers':
        return (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Customer Analysis Report</h3>
                <button
                  onClick={() => handleExportReport('customers', 'pdf')}
                  disabled={isLoading}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                  title="Export PDF"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export PDF
                </button>
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                  <span className="ml-2 text-gray-600">Loading report data...</span>
                </div>
              ) : (
                <ReportBody type="customers" data={reportData.customers} view={reportView} />
              )}
            </div>
          </div>
        );

      case 'products':
        return (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Product Performance Report</h3>
                <button
                  onClick={() => handleExportReport('products', 'pdf')}
                  disabled={isLoading}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                  title="Export PDF"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export PDF
                </button>
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                  <span className="ml-2 text-gray-600">Loading report data...</span>
                </div>
              ) : (
                <ReportBody type="products" data={reportData.products} view={reportView} />
              )}
            </div>
          </div>
        );

      case 'financial':
        return (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Financial Summary Report</h3>
                <button
                  onClick={() => handleExportReport('financial', 'pdf')}
                  disabled={isLoading}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                  title="Export PDF"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export PDF
                </button>
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                  <span className="ml-2 text-gray-600">Loading report data...</span>
                </div>
              ) : (
                <ReportBody type="financial" data={reportData.financial} view={reportView} />
              )}
            </div>
          </div>
        );

      case 'edi':
        return (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">EDI Performance Report</h3>
                <button
                  onClick={() => handleExportReport('edi', 'pdf')}
                  disabled={isLoading}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                  title="Export PDF"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export PDF
                </button>
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                  <span className="ml-2 text-gray-600">Loading report data...</span>
                </div>
              ) : (
                <ReportBody type="edi" data={reportData.edi} view={reportView} />
              )}
            </div>
          </div>
        );

      default:
        return (
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-gray-600">Select a section to view details.</p>
          </div>
        );
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
        <p className="text-gray-600 mt-2">
          Generate reports and view analytics based on your role and permissions
        </p>
      </div>

      {/* Natural-language query bar — works across all reports */}
      <div className="mb-6 overflow-hidden rounded-2xl bg-gradient-to-br from-primary-600 via-primary-700 to-indigo-800 p-5 shadow-lg">
        <div className="flex items-center gap-2 text-white/90">
          <Sparkles className="h-5 w-5" />
          <span className="text-sm font-semibold">Ask in plain English</span>
          <span className="hidden sm:inline text-xs text-white/60">— e.g. “approved chargebacks for customer Cardinal Health last month”</span>
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); runNlq(); }}
          className="mt-3 flex items-center gap-2 rounded-xl bg-white px-3 py-2 shadow-inner"
        >
          <Search className="h-5 w-5 flex-shrink-0 text-gray-400" />
          <input
            value={nlqInput}
            onChange={(e) => setNlqInput(e.target.value)}
            placeholder="Ask a question about your reports…"
            className="w-full bg-transparent text-gray-800 placeholder-gray-400 focus:outline-none"
            aria-label="Natural-language report query"
          />
          {(nlqInput || nlqResult) && (
            <button type="button" onClick={clearNlq} title="Clear" className="text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          )}
          <button
            type="submit"
            disabled={nlqLoading || !nlqInput.trim()}
            title="Run query"
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {nlqLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Run
          </button>
        </form>

        {/* Example chips */}
        {!nlqResult && !nlqError && (
          <div className="mt-3 flex flex-wrap gap-2">
            {NLQ_EXAMPLES.map((ex) => (
              <button
                key={ex}
                onClick={() => { setNlqInput(ex); runNlq(ex); }}
                className="rounded-full bg-white/15 px-3 py-1 text-xs text-white/90 hover:bg-white/25"
                title={`Try: ${ex}`}
              >
                {ex}
              </button>
            ))}
          </div>
        )}

        {/* Interpretation / errors */}
        {nlqError && (
          <div className="mt-3 flex items-start gap-2 rounded-lg bg-white/95 px-3 py-2 text-sm text-red-700">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" /> {nlqError}
          </div>
        )}
        {nlqResult && (
          <div className="mt-3 rounded-lg bg-white/95 px-3 py-2 text-sm text-gray-800">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-500">Showing:</span>
              <span className="font-semibold text-primary-800">{nlqResult.interpretation}</span>
            </div>
            {nlqResult.unmatched?.length > 0 && (
              <ul className="mt-1 list-disc pl-5 text-xs text-amber-700">
                {nlqResult.unmatched.map((u, i) => <li key={i}>{u}</li>)}
              </ul>
            )}
            {!NLQ_SUPPORTED_TABS.includes(nlqResult.applied?.tab) && (
              <p className="mt-1 text-xs text-gray-500">
                This maps to the {nlqResult.report?.name} report, which isn’t shown as a live tab here.
              </p>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Report Sections</h2>
            </div>
            <nav className="p-2">
              {accessibleSections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => handleTabChange(section.id)}
                  title={section.name}
                  className={`w-full text-left px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === section.id
                      ? 'bg-primary-100 text-primary-900 border-l-4 border-primary-500'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <div className="flex items-center">
                    <section.icon className="h-4 w-4 mr-3 flex-shrink-0" />
                    <span>{section.name}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 ml-7">{section.description}</p>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          {activeTab !== 'overview' && (
            <div className="mb-4 flex items-center justify-end">
              <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 shadow-sm">
                <button
                  onClick={() => setReportView('charts')}
                  title="Chart view"
                  className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${reportView === 'charts' ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  <BarChart3 className="h-4 w-4" /> Charts
                </button>
                <button
                  onClick={() => setReportView('data')}
                  title="Data table view"
                  className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${reportView === 'data' ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  <FileText className="h-4 w-4" /> Data
                </button>
              </div>
            </div>
          )}
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default Reports;
